import { v4 as uuidv4 } from 'uuid';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

import { experimentEligibilityReceived } from './experimentsSlice';
import { variantsFetchFailed, variantsReceived } from './variantsSlice';

import type { ExperimentClient } from '@amplitude/experiment-js-client';
import type { RemoteEvaluationClient } from '@amplitude/experiment-node-server';
import type { Request, Response } from 'express';
import type { Cookies } from 'react-cookie';
import type { Dispatch } from 'redux';

const logger = Logger.getLogger('wanderu-analytics');

// Data-driven bot-detection criteria. Add a new ASN or user-agent pattern by
// appending to the relevant array. Structural checks (missing user-agent,
// missing text/html in accept) live as hardcoded conditionals in
// getShouldInitAmplitude per spec FR-2.
const BOT_DETECTION_CONFIG = {
    blockedASNs: [15169, 199661, 36116, 8075, 714, 13238, 13414],
    blockedUserAgentPatterns: ['Google-Travel', 'Baiduspider-render', 'bingbot'],
    minBotScore: 5,
};

// Sensitive user properties that should only be sent server-side
interface SensitiveUserProperties {
    asn?: string;
    cfBotScore?: number;
    ipAddress?: string;
    ja4?: string;
}

// Client-safe user properties that can be returned in the API response
interface ClientUserProperties {
    accept?: string;
    acceptLanguage?: string;
    userAgent?: string;
}

// Combined properties for internal use
interface AllUserProperties extends ClientUserProperties, SensitiveUserProperties {}

// Matches Cloudflare list: https://dash.cloudflare.com/30203568562fe5fb374e30d278f53e3d/wanderu.com/caching/cache-rules/abdc0352ab324c39a579f27e12ddda25
const NON_CACHED_URLS = [
    '/account/',
    '/checkout/',
    '/depart/',
    '/explore/',
    '/return/',
    '/travel-review/',
    '/tripsummary/',
];

const isNonCachedUrl = (url: string) => {
    const urlToTest = url.split('?')[0];

    // homepage is not cached
    if (urlToTest === '/' || /^\/[a-z]{2}-[a-z]{2}\/$/.test(urlToTest)) return true;

    // explore landing page with no url segments after it is cached
    if (urlToTest.endsWith('/explore/')) return false;

    return NON_CACHED_URLS.some((segment) => urlToTest.includes(segment));
};

let experiment: RemoteEvaluationClient;
let amplitudeNodeInitialized = false;

type InitBlockReason = string | null;
type ShouldInitResult = [boolean, InitBlockReason, AllUserProperties];

function getShouldInitAmplitude(req: Request): ShouldInitResult {
    const asn = req?.headers?.['x-visitor-asn'] as string | undefined;
    const cfBotScore = Number(req?.headers?.['cf-bot-score']);
    const ipAddress = req?.headers?.['x-real-ip'] as string | undefined;
    const ja4 = req?.headers?.['cf-ja4'] as string | undefined;
    const userAgent = req?.headers?.['user-agent'] || '';
    const acceptLanguage = req?.headers?.['accept-language'];
    const accept = req?.headers?.accept;

    const userProperties = {
        accept,
        acceptLanguage,
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        userAgent,
    };

    // Structural: user-agent header missing or empty
    if (!userAgent) {
        return [false, 'missing_user_agent', userProperties];
    }

    // Structural: accept header must contain text/html
    if (!accept || !accept.includes('text/html')) {
        return [false, 'missing_accept_text_html', userProperties];
    }

    // Data-driven: Cloudflare bot score below minBotScore.
    // 0 is falsy which is fine because that means "not computed" by Cloudflare.
    if (cfBotScore && cfBotScore < BOT_DETECTION_CONFIG.minBotScore) {
        return [false, 'cf_bot_score', userProperties];
    }

    // Data-driven: ASN matches the blocklist.
    if (asn && BOT_DETECTION_CONFIG.blockedASNs.includes(Number(asn))) {
        return [false, 'blocked_asn', userProperties];
    }

    // Data-driven: user-agent matches any blocked pattern (substring match).
    const hasMatchingUserAgentPattern = BOT_DETECTION_CONFIG.blockedUserAgentPatterns
        .some((pattern) => userAgent.includes(pattern));
    if (hasMatchingUserAgentPattern) {
        return [false, 'user_agent_pattern', userProperties];
    }

    return [true, null, userProperties];
}

interface FetchServerVariantsParams {
    cookies: Cookies;
    dispatch: Dispatch;
    req: Request;
    res: Response;
}

const amplitudeLoggerProvider = {
    debug: (message: string, ...additionalInfo: []) => {
        logger.debug('amplitudeLoggerProvider', message, additionalInfo);
    },

    error: (message: string, ...additionalInfo: []) => {
        logger.error('amplitudeLoggerProvider', message, additionalInfo);
    },

    info: (message: string, ...additionalInfo: []) => {
        logger.info('amplitudeLoggerProvider', message, additionalInfo);
    },

    warn: (message: string, ...additionalInfo: []) => {
        logger.warn('amplitudeLoggerProvider', message, additionalInfo);
    },
};

interface ServerSideIdentifyParams {
    deviceId: string;
    initBlockReason: InitBlockReason;
    shouldInitAmplitude: boolean;
    sensitiveProperties: SensitiveUserProperties;
}

async function sendServerSideAmplitudeIdentify({
    deviceId,
    initBlockReason,
    shouldInitAmplitude,
    sensitiveProperties,
}: ServerSideIdentifyParams) {
    const apiKey = appSettings?.config?.amplitude?.apiKey;

    if (!apiKey || typeof window !== 'undefined') {
        return;
    }

    try {
        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        const { Identify, identify, init } = require('@amplitude/analytics-node');

        if (!amplitudeNodeInitialized) {
            init(apiKey, {
                flushIntervalMillis: 0,
                flushQueueSize: 1,
            });
            amplitudeNodeInitialized = true;
        }

        const identifyEvent = new Identify();

        identifyEvent.set('should_init_amplitude', shouldInitAmplitude);

        if (initBlockReason) {
            identifyEvent.set('init_block_reason', initBlockReason);
        }

        const {
            asn,
            cfBotScore,
            ipAddress,
            ja4,
        } = sensitiveProperties;

        if (cfBotScore) identifyEvent.set('cloudflare_bot_score', cfBotScore);
        if (asn) identifyEvent.set('asn', asn);
        if (ipAddress) identifyEvent.set('ip_address', ipAddress);
        if (ja4) identifyEvent.set('ja4', ja4);

        await identify(identifyEvent, { device_id: deviceId });

        logger.debug('serverSideAmplitudeIdentify', 'sent', { deviceId });
    } catch (error) {
        logger.error('serverSideAmplitudeIdentify', 'failed', { deviceId, error });
    }
}

async function fetchServerVariants({
    cookies,
    dispatch,
    req,
    res,
}: FetchServerVariantsParams) {
    const isNonCachedPage = isNonCachedUrl(req.url);
    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const isServerSide = typeof window === 'undefined';

    if (!apiKey || !isServerSide || !isNonCachedPage) {
        return;
    }

    const deviceId = await handleAmplitudeCookies(cookies, res);

    const [
        shouldInitAmplitude,
        initBlockReason,
        allUserProperties,
    ] = getShouldInitAmplitude(req);

    // Extract sensitive properties for server-side tracking only
    const {
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        ...clientSafeProperties
    } = allUserProperties;

    // Send sensitive data to Amplitude server-side for ALL users (eligible and
    // ineligible). Drop filters in Amplitude depend on these properties being
    // present on the user profile (spec FR-9 / AC-21 / AC-22). Server-side
    // Identify is free and does not consume event volume.
    sendServerSideAmplitudeIdentify({
        deviceId,
        initBlockReason,
        sensitiveProperties: {
            asn,
            cfBotScore,
            ipAddress,
            ja4,
        },
        shouldInitAmplitude,
    });

    // Dispatch only client-safe data to Redux (this gets serialized to client)
    dispatch(experimentEligibilityReceived({
        deviceId,
        shouldInitAmplitude,
        userProperties: clientSafeProperties,
    }));

    if (!shouldInitAmplitude) {
        return;
    }

    if (!experiment) {
        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        const { Experiment: AmplitudeExperiment, LogLevel } = require('@amplitude/experiment-node-server');

        const config = {
            fetchRetries: 1,
            fetchRetryBackoffMinMillis: 0,
            fetchRetryTimeoutMillis: 500,
            fetchTimeoutMillis: 500,
            loggerProvider: amplitudeLoggerProvider,
            logLevel: LogLevel.Error,
        };

        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        experiment = AmplitudeExperiment
            .initializeRemote(apiKey, config);
    }

    try {
        const variants = await experiment
            .fetchV2({ device_id: deviceId });

        dispatch(variantsReceived({ variants }));
    } catch (error) {
        // Don't dispatch variantsFetchFailed() here — it sets hasFetched: true,
        // which prevents the client from retrying. By leaving hasFetched as false,
        // the client's AmplitudeProvider will fall back to fetching variants itself.
        logger.error('Amplitude', 'server_fetch_failed', {
            deviceId,
            error,
        });
    }
}

interface FetchClientVariantsParams {
    experiment: ExperimentClient;
    deviceId: string | undefined;
    dispatch: Dispatch;
}

async function fetchClientVariants({
    deviceId,
    dispatch,
    experiment: clientExperiment,
}: FetchClientVariantsParams) {
    try {
        await clientExperiment.fetch({ device_id: deviceId });
    } catch (error) {
        logger.error('Amplitude', 'fetch_failed', {
            deviceId,
            error,
        });
        dispatch(variantsFetchFailed());
        return;
    }

    const variants = clientExperiment.all();

    dispatch(variantsReceived({ variants }));
}

interface FetchExperimentEligibilityFromRequestParams {
    cookies: Cookies;
    req: Request;
    res: Response;
}

async function fetchExperimentEligibilityFromRequest({
    cookies,
    req,
    res,
}: FetchExperimentEligibilityFromRequestParams) {
    const deviceId = await handleAmplitudeCookies(cookies, res);
    const [
        shouldInitAmplitude,
        initBlockReason,
        allUserProperties,
    ] = getShouldInitAmplitude(req);

    // Extract sensitive properties for server-side tracking only
    const {
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        ...clientSafeProperties
    } = allUserProperties;

    // Send eligibility data to Amplitude server-side (non-blocking). Sensitive
    // properties are sent for ALL users (eligible and ineligible) so Amplitude
    // drop filters can match on them. Server-side Identify is free and does
    // not count toward event volume (spec FR-9 / AC-21 / AC-22).
    sendServerSideAmplitudeIdentify({
        deviceId,
        initBlockReason,
        sensitiveProperties: {
            asn,
            cfBotScore,
            ipAddress,
            ja4,
        },
        shouldInitAmplitude,
    });

    // Return only client-safe data. initBlockReason is intentionally not
    // included so bot operators cannot learn why they were flagged
    // (spec FR-8 / AC-25). deviceId, shouldInitAmplitude, userProperties only.
    return {
        deviceId,
        shouldInitAmplitude,
        userProperties: clientSafeProperties,
    };
}

async function fetchExperimentEligibility() {
    const response = await fetch('/api/experiment-eligibility', {
        credentials: 'include',
        headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
        throw new Error(`Eligibility request failed: ${response.status}`);
    }

    return response.json();
}

async function handleAmplitudeCookies(cookies: Cookies, res: Response) { // https://amplitude.com/docs/sdks/experiment-sdks/experiment-node-js#access-amplitude-cookies
    let deviceId: string | undefined;

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
    const { AmplitudeCookie } = require('@amplitude/experiment-node-server');

    // Get the cookie name for the Amplitude API key
    const ampCookieName = AmplitudeCookie.cookieName(apiKey, true);

    const cookie = cookies.get(ampCookieName);
    // Try to get device ID from existing cookie
    if (cookie) {
        deviceId = AmplitudeCookie.parse(cookie, true).device_id;
    }

    // If no device ID found, generate a new one and set the cookie
    if (!deviceId) {
        deviceId = uuidv4();

        const ampCookieValue = AmplitudeCookie.generate(deviceId, true);

        cookies.set(ampCookieName, ampCookieValue);
        res.cookie(ampCookieName, ampCookieValue, {
            domain: '.wanderu.com', // this should be the same domain used by the Amplitude JS SDK
            httpOnly: false,
            secure: false,
        });
    }

    return deviceId;
}

export {
    BOT_DETECTION_CONFIG,
    fetchClientVariants,
    fetchExperimentEligibility,
    fetchExperimentEligibilityFromRequest,
    fetchServerVariants,
    getShouldInitAmplitude,
    isNonCachedUrl,
};
